import * as React from 'react';

import { Form, Row, Spin } from 'antd';
import { AimOutlined, Input, Space, Tooltip } from 'farmhub-ui-core';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { RootState, useAppSelector } from 'app';
import { LocationSelectMobile, LocationSelectV1 } from 'components';
import { findLocationValue, resetLocationSearch, selectAppDevice } from 'slice';
import { theme } from 'styled';
import { DEVICES } from 'utils';
import {
  getCurrentLocation,
  isVietnamesePhoneNumber,
  validateEmail,
} from 'utils/function';

const DivRow = styled.div`
  width: 100%;
`;

interface EntityInfoStepProps {
  form: any;
  isValidEmail: boolean;
  setIsValidEmail: (e: boolean) => void;
  isValidPhoneNumber: boolean;
  setIsValidPhoneNumber: (e: boolean) => void;
  onSetBodyUpdate?: (e: any) => void;
  hasErrorCode?: boolean;
  setHasErrorCode?: (e: boolean) => void;
}

const EntityInfoStep = ({
  form,
  isValidEmail,
  setIsValidEmail,
  isValidPhoneNumber,
  setIsValidPhoneNumber,
  onSetBodyUpdate,
  hasErrorCode,
  setHasErrorCode,
}: EntityInfoStepProps): JSX.Element => {
  const dispatch = useDispatch();
  const appDevice = useAppSelector(selectAppDevice);
  const [isLoadingAddress, setIsLoadingAddress] = React.useState(false);
  const findLocation = useSelector(
    (state: RootState) => state.location.findLocation
  );

  const handleVerifyEmail = (e): void => {
    setIsValidEmail(validateEmail(e.target.value));
  };

  const handleVerifyPhone = (e): void => {
    setIsValidPhoneNumber(isVietnamesePhoneNumber(e.target.value));
  };

  const handleChangeIsLoadingAddress = (e: boolean): void => {
    setIsLoadingAddress(e);
  };

  const handleGetLocationCode = (value: any): void => {
    dispatch(
      findLocationValue({
        districtName: value.districtName,
        provinceName: value.provinceName,
        wardName: value.wardName,
        address: value.address,
        latitude: value.latitude,
        longitude: value.longitude,
      })
    );
  };

  React.useEffect(() => {
    if (findLocation) {
      const bodyUpdate = {
        address: findLocation.address,
        provinceCode: findLocation.provinceCode,
        districtCode: findLocation.districtCode,
        wardCode: findLocation.wardCode,
        latitude: findLocation.latitude,
        longitude: findLocation.longitude,
      };
      form.setFieldsValue(bodyUpdate);
      onSetBodyUpdate && onSetBodyUpdate(bodyUpdate);
      setIsLoadingAddress(false);
      dispatch(resetLocationSearch());
    }
  }, [dispatch, findLocation, form, onSetBodyUpdate]);

  return (
    <>
      <CForm
        className="login-form"
        form={form}
        layout="vertical"
        name="add_address_form"
        style={{ width: appDevice.device === DEVICES.DESKTOP ? 420 : '100%' }}
      >
        <Form.Item
          label="Tên cửa hàng"
          name="fullName"
          rules={[
            {
              required: true,
              message: 'Bạn chưa nhập tên cửa hàng!',
            },
          ]}
        >
          <Input height={theme.input.height.md} placeholder="Tên cửa hàng" />
        </Form.Item>
        <Form.Item
          help={
            isValidEmail ? null : (
              <span style={{ color: 'red' }}>Email không hợp lệ</span>
            )
          }
          label="Email"
          name="email"
          rules={[
            {
              required: true,
              message: 'Bạn chưa nhập email!',
            },
          ]}
        >
          <Input
            height={theme.input.height.md}
            placeholder="Email"
            onChange={handleVerifyEmail}
          />
        </Form.Item>
        <Form.Item
          help={
            isValidPhoneNumber ? null : (
              <span style={{ color: 'red' }}>Số điện thoại không hợp lệ!</span>
            )
          }
          label="Số điện thoại"
          name="phoneNumber"
          rules={[
            {
              required: true,
              message: 'Bạn chưa nhập số điện thoại!',
            },
          ]}
        >
          <Input
            height={theme.input.height.md}
            placeholder="Số điện thoại"
            onChange={handleVerifyPhone}
          />
        </Form.Item>
        <Row gutter={[0, 0]}>
          {appDevice.device === DEVICES.DESKTOP ? (
            <LocationSelectV1
              required
              districtCodeInit={form.getFieldValue('districtCode')}
              form={form}
              hasErrorCode={hasErrorCode}
              idPopup="normal_login"
              isLoadingAddress={isLoadingAddress}
              marginBottom={0}
              provinceCodeInit={form.getFieldValue('provinceCode')}
              setHasErrorCode={setHasErrorCode}
              showTitle={false}
              wardCodeInit={form.getFieldValue('wardCode')}
            />
          ) : (
            <>
              <LocationSelectMobile
                districtCodeInit={form.getFieldValue('districtCode')}
                form={form}
                hasErrorCode={hasErrorCode}
                height={theme.select.height.sm}
                idPopup="normal_login"
                isLoadingAddress={isLoadingAddress}
                isNotShowLabel={false}
                marginBottom={0}
                provinceCodeInit={form.getFieldValue('provinceCode')}
                setHasErrorCode={setHasErrorCode}
                wardCodeInit={form.getFieldValue('wardCode')}
              />
              <Space height={18} />
            </>
          )}
        </Row>
        <Form.Item
          label="Địa chỉ"
          name="address"
          rules={[
            {
              required: true,
              message: 'Bạn chưa nhập địa chỉ!',
            },
          ]}
        >
          <Input
            height={theme.input.height.md}
            placeholder="Địa chỉ"
            suffix={
              <Tooltip title="Lấy vị trí hiện tại">
                {!isLoadingAddress ? (
                  <AimOutlined
                    style={{
                      color: theme.colors.primary.main,
                      fontSize: '20px',
                    }}
                    onClick={() =>
                      getCurrentLocation(
                        handleChangeIsLoadingAddress,
                        handleGetLocationCode
                      )
                    }
                  />
                ) : (
                  <Spin
                    style={{
                      color: theme.colors.primary.main,
                      fontSize: '20px',
                    }}
                  />
                )}
              </Tooltip>
            }
          />
        </Form.Item>
        <DivRow />
      </CForm>
      <Space height={12} />
    </>
  );
};

export default EntityInfoStep;

export const CForm = styled(Form)`
  .ant-row.ant-form-item {
    width: 100%;

    .ant-col.ant-form-item-label {
      label {
        font-weight: ${p => p.theme.font.weight.medium};
        color: ${p => p.theme.colors.text.primary};
      }
    }

    .ant-col.ant-form-item-control {
      width: 100%;
      flex: 0;
    }
  }
`;
